import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from 'reactflow';


const ResponseEdge = React.memo(function ResponseEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  selected,
}) {
  const [value, setValue] = useState(data?.label || '');
  const lastExternalLabelRef = useRef(data?.label || '');
  const isEditingRef = useRef(false);

  // Track editing state
  useEffect(() => {
    isEditingRef.current = data?.isEditing || false;
  }, [data?.isEditing]);

  // Smart sync: only update local value when external label changes and we're not editing
  useEffect(() => {
    const newLabel = data?.label || '';
    const wasExternalChange = newLabel !== lastExternalLabelRef.current;
        
    if (wasExternalChange && !isEditingRef.current) {
      setValue(newLabel);
    }
        
    // Always update the ref to track the latest external value
    lastExternalLabelRef.current = newLabel;
  }, [data?.label]);

  const parallelIndex = data?.parallelIndex || 0;

  let path, labelX, labelY;

  if (parallelIndex === 0) {
    [path, labelX, labelY] = getSmoothStepPath({
      sourceX, sourceY, sourcePosition,
      targetX, targetY, targetPosition,
      borderRadius: 15,
      offset: 30,
    });
  } else {
    const spacing = 28;
    const baseGap = 30;
    const r = 12;
    const midY = sourceY + baseGap + parallelIndex * spacing;
    const dx = targetX > sourceX ? 1 : targetX < sourceX ? -1 : 0;

    if (dx === 0) {
      const sideOffset = 60 + parallelIndex * 30;
      const routeX = sourceX + sideOffset;
      path = `M ${sourceX} ${sourceY}`
        + ` L ${sourceX} ${midY - r}`
        + ` Q ${sourceX} ${midY} ${sourceX + r} ${midY}`
        + ` L ${routeX - r} ${midY}`
        + ` Q ${routeX} ${midY} ${routeX} ${midY + r}`
        + ` L ${routeX} ${targetY - baseGap - r}`
        + ` Q ${routeX} ${targetY - baseGap} ${routeX - r} ${targetY - baseGap}`
        + ` L ${targetX + r} ${targetY - baseGap}`
        + ` Q ${targetX} ${targetY - baseGap} ${targetX} ${targetY - baseGap + r}`
        + ` L ${targetX} ${targetY}`;
      labelX = (sourceX + routeX) / 2;
      labelY = midY;
    } else {
      const clampedR = Math.min(r, Math.abs(targetX - sourceX) / 2, Math.abs(midY - sourceY) / 2);
      path = `M ${sourceX} ${sourceY}`
        + ` L ${sourceX} ${midY - clampedR}`
        + ` Q ${sourceX} ${midY} ${sourceX + dx * clampedR} ${midY}`
        + ` L ${targetX - dx * clampedR} ${midY}`
        + ` Q ${targetX} ${midY} ${targetX} ${midY + clampedR}`
        + ` L ${targetX} ${targetY}`;
      labelX = (sourceX + targetX) / 2;
      labelY = midY;
    }
  }
    
  const handleCommit = useCallback(() => {
    if (data?.onLabelChange) {
      data.onLabelChange(value);
    }
  }, [data, value]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleCommit();
    } else if (e.key === 'Escape') {
      if (data?.onLabelEditEnd) {
        data.onLabelEditEnd();
      }
    }
  }, [handleCommit, data]);

  const handleDelete = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data?.onDelete) {
      data.onDelete();
    }
  }, [data]);

  return (
    <>
      <BaseEdge
        path={path}
        markerEnd={markerEnd}
        style={{
          stroke: selected ? '#3b82f6' : '#9ca3af',
          strokeWidth: selected ? 3 : 2,
          zIndex: selected ? 10 : 1,
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
            zIndex: selected ? 10010 : 10000,
            minWidth: '60px',
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="nodrag nopan"
        >
          {data?.isEditing ? (
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                background: 'white', 
                padding: '6px 8px', 
                borderRadius: '8px', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '1px solid #e5e7eb',
                minWidth: '120px'
              }}
            >
              <input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleCommit}
                onKeyDown={handleKeyDown}
                style={{ 
                  padding: '4px 8px', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px', 
                  fontSize: '13px',
                  flex: 1,
                  outline: 'none'
                }}
                placeholder="Response label..."
              />
              <button
                title="Delete connection"
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={handleDelete}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{ 
                  background: '#ef4444', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '6px', 
                  width: '24px', 
                  height: '24px', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  pointerEvents: 'auto',
                  zIndex: 1000
                }}
              >
                                ×
              </button>
            </div>
          ) : (
            <div 
              style={{ 
                background: selected ? '#dbeafe' : 'white', 
                padding: '6px 12px', 
                borderRadius: '16px', 
                border: selected ? '2px solid #3b82f6' : '1px solid #d1d5db', 
                fontSize: '12px', 
                color: selected ? '#1e40af' : '#374151', 
                minWidth: '60px', 
                textAlign: 'center',
                fontWeight: selected ? '500' : '400',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: selected ? '0 2px 8px rgba(59, 130, 246, 0.2)' : '0 1px 4px rgba(0,0,0,0.12)',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = selected ? '#bfdbfe' : '#f3f4f6';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = selected ? '#dbeafe' : '#f9fafb';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {data?.label || data?.responseKey || 'response'}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
});

export default ResponseEdge;


